// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, getRedirectResult } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import Navigation from './components/Navigation';
import Home from './components/Home';
import Documents from './components/Documents';
import CompleteProfile from './components/CompleteProfile';
import License from './components/License';
import PublicLicense from './components/PublicLicense';
import ForceLogout from './components/ForceLogout';
import RepairUserProfile from './components/RepairUserProfile';
import DeletionStatus from './components/DataDeletion';
import './index.css';

// Wrapper for Logout handler with navigation
function LogoutHandler({ children }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    signOut(auth).then(() => {
      // Redirect to home page after logout
      navigate('/');
    }).catch((error) => {
      console.error("Error logging out:", error);
    });
  };
  
  return children(handleLogout);
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Handle redirect result (for mobile auth)
  useEffect(() => {
    const handleRedirectResult = async () => {
      console.log("Checking for redirect result");
      // Check if we're expecting a redirect result
      const isPending = localStorage.getItem('authRedirectPending') === 'true';
      console.log("Redirect pending?", isPending);
      
      if (!isPending) {
        console.log("No pending redirect, skipping processing");
        return; // No pending redirect, skip processing
      }
      
      try {
        console.log("Processing redirect result");
        
        const result = await getRedirectResult(auth);
        console.log("Redirect result:", result ? "Success" : "No result");
        
        if (result) {
          // User successfully logged in after redirect
          const user = result.user;
          console.log("User authenticated after redirect:", user.uid);
          
          // Clear the pending flag
          localStorage.removeItem('authRedirectPending');
          
          // Determine provider type
          let providerType = 'unknown';
          if (user.providerData && user.providerData.length > 0) {
            const providerId = user.providerData[0].providerId;
            if (providerId === 'facebook.com') {
              providerType = 'facebook';
            } else if (providerId === 'google.com') {
              providerType = 'google';
            } else if (providerId === 'apple.com') {
              providerType = 'apple';
            }
          }
          
          // Check if this user already has a profile
          const userRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(userRef);
          
          if (!docSnap.exists()) {
            console.log("Creating new user profile");
            // Create new user profile
            const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            await setDoc(userRef, {
              firstName,
              lastName,
              email: user.email || '',
              country: '',
              licenseAgreed: false,
              registrationDate: Timestamp.now(),
              yearlyBoardCount: 0,
              lastResetDate: Timestamp.now(),
              authProvider: providerType
            });
            
            console.log("Redirecting to complete profile");
            // Add a brief delay before redirecting
            setTimeout(() => {
              window.location.href = "/complete-profile";
            }, 500);
          } else {
            // User exists, check if profile is complete
            const userData = docSnap.data();
            console.log("User profile exists:", userData.licenseAgreed ? "License agreed" : "License not agreed");
            
            if (!userData.licenseAgreed || !userData.country) {
              console.log("Redirecting to complete profile");
              setTimeout(() => {
                window.location.href = "/complete-profile";
              }, 500);
            } else {
              console.log("Redirecting to documents");
              setTimeout(() => {
                window.location.href = "/documents";
              }, 500);
            }
          }
        } else {
          // If we got here with a pending flag but no result,
          // it could mean the redirect failed silently
          console.log("No redirect result but pending flag was set - clearing flag");
          localStorage.removeItem('authRedirectPending');
        }
      } catch (error) {
        console.error("Error with redirect auth:", error);
        // Clear the flag even on error, to prevent getting stuck
        localStorage.removeItem('authRedirectPending');
      }
    };
    
    // Only run when the app loads
    handleRedirectResult();
  }, []);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <Router>
      <LogoutHandler>
        {(handleLogout) => (
          <div className="min-h-screen bg-white">
            <Navigation user={user} handleLogout={handleLogout} />
            
            <Routes>
              <Route path="/" element={<Home user={user} />} />
              <Route path="/license" element={<License />} />
              <Route path="/view-license" element={<PublicLicense />} />
              <Route path="/documents" element={<Documents user={user} />} />
              <Route path="/force-logout" element={<ForceLogout />} />
              <Route path="/repair-profile" element={<RepairUserProfile user={user} />} />
              <Route path="/deletion-status/:code" element={<DeletionStatus />} />
              <Route path="/complete-profile" element={<CompleteProfile user={user} />} />
            </Routes>
          </div>
        )}
      </LogoutHandler>
    </Router>
  );
}

export default App;